import { ReactNode } from 'react';
import { LoadingAnimation } from '@instamenu/ui-icons';
import {
  buttonRowStyle,
  buttonSizeVariant,
  buttonStyle,
  buttonVariantsStyle,
  rectangularButtonStyle,
} from './button.css';
import clsx from 'clsx';
import { themeVars } from '@instamenu/ui-vanilla-extract';

export type ButtonOwnProps<E extends React.ElementType = React.ElementType> = {
  as?: E;
  children?: ReactNode;
  css?: React.CSSProperties;
  variant?: keyof typeof buttonVariantsStyle;
  loading?: boolean;
  size?: keyof typeof buttonSizeVariant;
  row?: boolean;
  containerStyle?: React.CSSProperties;
  rectangular?: boolean;
  className?: string;
};

export type ButtonProps<E extends React.ElementType = 'button'> = ButtonOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof ButtonOwnProps>;

export const Button = <E extends React.ElementType = 'button'>({
  as,
  children,
  loading,
  variant,
  size,
  css,
  containerStyle,
  rectangular,
  className,
  ...rest
}: ButtonProps<E>) => {
  const Component = as || 'button';

  const buttonStyleProps = variant;
  const buttonSizeProps = size;
  const buttonLoading = loading;

  const ButtonStyleVariant = buttonLoading
    ? buttonVariantsStyle['loading']
    : buttonStyleProps
      ? buttonVariantsStyle[buttonStyleProps]
      : buttonVariantsStyle.primary;

  const RectangularButtonStyle = rectangular ? rectangularButtonStyle : undefined;

  const ButtonSize = buttonSizeProps ? buttonSizeVariant[buttonSizeProps] : buttonSizeVariant.md;

  return (
    <Component
      className={clsx(
        buttonStyle,
        ButtonStyleVariant,
        ButtonSize,
        RectangularButtonStyle,
        className,
      )}
      type="button"
      style={css}
      {...rest}
    >
      {loading && (
        <LoadingAnimation
          data-testid="loading-animation"
          color={themeVars.color.brand}
          style={{
            width: 40,
            height: 30,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            position: 'absolute',
          }}
        />
      )}
      <div style={{ display: 'flex', opacity: loading ? 0 : 1, ...containerStyle }}>{children}</div>
    </Component>
  );
};

export const Submit = (props: ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return <Button type="submit" {...props} />;
};

export const ButtonRow = ({ children }: { children: ReactNode }) => {
  return <div className={buttonRowStyle}>{children}</div>;
};
